import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4m269rifile0aqbfldg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-rose-50 via-pink-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              تواصلي <span className="text-rose-400">معنا</span>
            </h1>
            <p className="text-lg text-gray-600">
              نحن هنا للإجابة على جميع استفساراتك ومساعدتك في اختيار المنتجات المناسبة
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                معلومات التواصل
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-rose-400"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">البريد الإلكتروني</h3>
                    <a href="mailto:info@rosemist.com" className="text-gray-600 hover:text-rose-400 transition-colors">
                      info@rosemist.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-rose-400"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">رقم الهاتف</h3>
                    <a href="tel:+966501234567" className="text-gray-600 hover:text-rose-400 transition-colors">
                      +966 50 123 4567
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-rose-400"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">العنوان</h3>
                    <p className="text-gray-600">
                      الرياض، المملكة العربية السعودية
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-rose-400"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">ساعات العمل</h3>
                    <p className="text-gray-600">
                      السبت - الخميس: 9:00 صباحاً - 9:00 مساءً<br />
                      الجمعة: 2:00 مساءً - 9:00 مساءً
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  تابعينا على
                </h3>
                <div className="flex items-center gap-4">
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-instagram-line text-xl"></i>
                  </a>
                  <a 
                    href="https://facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a 
                    href="https://twitter.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-xl"></i>
                  </a>
                  <a 
                    href="https://tiktok.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-tiktok-line text-xl"></i>
                  </a>
                  <a 
                    href="https://snapchat.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-snapchat-line text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8 lg:p-10">
              <h2 className="text-3xl font-bold text-gray-800 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                أرسلي لنا رسالة
              </h2>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
                      الاسم الكامل *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 transition-colors text-sm"
                      placeholder="أدخلي اسمك الكامل"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                      البريد الإلكتروني *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 transition-colors text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 transition-colors text-sm"
                      placeholder="+966 50 123 4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-bold text-gray-700 mb-2">
                      الموضوع *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 transition-colors text-sm"
                    >
                      <option value="">اختاري الموضوع</option>
                      <option value="استفسار عن منتج">استفسار عن منتج</option>
                      <option value="طلب مساعدة">طلب مساعدة</option>
                      <option value="شكوى">شكوى</option>
                      <option value="اقتراح">اقتراح</option>
                      <option value="أخرى">أخرى</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">
                      الرسالة *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 transition-colors resize-none text-sm"
                      placeholder="اكتبي رسالتك هنا... (الحد الأقصى 500 حرف)"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-2">
                      {formData.message.length}/500 حرف
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-rose-400 to-pink-400 text-white py-4 rounded-xl font-bold hover:from-rose-500 hover:to-pink-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'جاري الإرسال...' : 'إرسال الرسالة'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-3.5 rounded-xl text-sm">
                      <i className="ri-checkbox-circle-line ml-2"></i>
                      تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3.5 rounded-xl text-sm">
                      <i className="ri-error-warning-line ml-2"></i>
                      حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-br from-rose-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              موقعنا
            </h2>
            <p className="text-gray-600">
              زورينا في متجرنا للحصول على استشارة شخصية وتجربة المنتجات
            </p>
          </div>
          
          <div className="rounded-3xl overflow-hidden shadow-xl h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.2!2d46.6753!3d24.7136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzQ5LjAiTiA0NsKwNDAnMzEuMSJF!5e0!3m2!1sar!2ssa!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="موقع Rose Mist"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
